import { sdk } from '@/services/sdk';
import { idOf, logActivity, rows } from '@/shared/data';
import {
  GIFT_MONTHS_PER_REFERRAL,
  findReferrerLine,
  latestLineOf,
  normUsername,
  qualifiesForReferral,
} from '@/shared/referrals';

const { and, eq, isIn, isNotNull } = window.ApperSDK.CoreSDK;

/**
 * Count one use of the order's discount code, once per order.
 * Customers can't write discount codes, so this runs from the console when an order is
 * invoiced or activated; `discount_counted_c` on the order stops it counting twice.
 * Best-effort: a failure here never blocks the order action.
 */
export async function countDiscountUse(order) {
  const code = String(order?.discount_code_c ?? '').trim();
  if (!code || order?.discount_counted_c || !(Number(order?.discount_c) > 0)) return false;
  try {
    // Re-read the flag: the order on screen may be older than the last action that counted it.
    const fresh = await sdk.table('orders_c').select(['discount_counted_c']).get(order.Id);
    if (fresh?.data?.discount_counted_c) return false;
    const res = await sdk
      .table('discount_codes_c')
      .select(['Name', 'uses_c'])
      .where(eq('Name', code.toUpperCase()))
      .limit(1)
      .fetch();
    const found = res?.data?.[0];
    if (found) {
      const up = await sdk.table('discount_codes_c').update({ Id: found.Id, uses_c: Number(found.uses_c || 0) + 1 });
      if (!up?.data?.length) return false;
    }
    await sdk.table('orders_c').update({ Id: order.Id, discount_counted_c: true });
    return !!found;
  } catch {
    return false;
  }
}

const sameText = (a, b) => !!a && !!b && String(a).trim().toLowerCase() === String(b).trim().toLowerCase();

/** The customer a code belongs to: the code's linked Referrer, else the customer whose own referral code it is. */
async function referrerFor(code) {
  const dc = await sdk.table('discount_codes_c').select(['Name', 'customer_c']).where(eq('Name', code)).limit(1).fetch();
  const linked = idOf(dc?.data?.[0]?.customer_c);
  if (linked) return linked;
  const c = await sdk.table('customers_c').select(['Name']).where(eq('referral_code_c', code)).limit(1).fetch();
  return c?.data?.[0]?.Id ?? null;
}

/**
 * Credit the referrer once a friend's order goes live.
 * A referral is an order of 6 months or longer, placed with an active, paid line's username
 * (`referred_by_c`) or with a referral code (a discount code linked to a Referrer, or a
 * customer's own REF- code), that gets activated. The referrer's count goes up and their line
 * is owed one free month (`gift_months_c`) for the team to gift.
 * Runs from the console on activation; `referral_counted_c` stops it counting twice.
 * Using your own username or code never counts. Best-effort: never blocks the order action.
 */
export async function countReferral(order) {
  const code = String(order?.discount_code_c ?? '').trim().toUpperCase();
  const handle = normUsername(order?.referred_by_c);
  if ((!code && !handle) || order?.referral_counted_c) return false;
  try {
    const fresh = await sdk
      .table('orders_c')
      .select(['referral_counted_c', 'customer_c', 'email_c', 'plan_c', 'term_c', 'referred_by_c', 'line_username_c'])
      .get(order.Id);
    const now = fresh?.data ?? order;
    if (now.referral_counted_c || !qualifiesForReferral(now)) return false;

    let line = handle ? await findReferrerLine(handle) : null;
    if (line && String(line.Id) === String(order.Id)) line = null;
    let referrerId = idOf(line?.customer_c);
    let via = line ? `username ${handle}` : '';
    if (!line && code) {
      referrerId = await referrerFor(code);
      if (referrerId) {
        line = await latestLineOf(referrerId);
        via = `code ${code}`;
      }
    }
    if (!line && !referrerId) return false;

    const ref = referrerId
      ? await sdk.table('customers_c').select(['Name', 'email_c', 'referrals_earned_c']).get(referrerId)
      : null;
    const buyerId = idOf(now.customer_c ?? order.customer_c);
    const buyerEmail = now.email_c ?? order.email_c;
    const self =
      (buyerId && referrerId && String(buyerId) === String(referrerId)) ||
      sameText(buyerEmail, ref?.data?.email_c) ||
      sameText(buyerEmail, line?.email_c) ||
      (handle && sameText(handle, now.line_username_c));

    // Flag first so a retry can never count the same order twice.
    const mark = await sdk.table('orders_c').update({ Id: order.Id, referral_counted_c: true });
    if (!mark?.data?.length || self) return false;

    if (ref?.data) {
      await sdk
        .table('customers_c')
        .update({ Id: referrerId, referrals_earned_c: Number(ref.data.referrals_earned_c || 0) + 1 });
    }
    if (line) {
      await sdk
        .table('orders_c')
        .update({ Id: line.Id, gift_months_c: Number(line.gift_months_c || 0) + GIFT_MONTHS_PER_REFERRAL });
    }
    const who = ref?.data?.Name || line?.Name || 'a customer';
    logActivity(
      'Code',
      `Referral earned by ${who}`,
      order.Name,
      `${via}${line ? ` · ${GIFT_MONTHS_PER_REFERRAL} free month owed on ${line.Name}` : ' · no live line to gift'}`,
    );
    return true;
  } catch {
    return false;
  }
}

/**
 * Rebuild a customer's referral count from the orders themselves: live orders of 6 months or
 * longer placed with their line usernames, their REF- code or any discount code linked to them,
 * not their own. Marks those orders as counted. Free months already owed are left alone.
 * Returns the new count, or throws with a readable message.
 */
export async function recountReferrals(customer) {
  const codes = new Set();
  if (customer?.referral_code_c) codes.add(String(customer.referral_code_c).trim().toUpperCase());
  const [linked, own] = await Promise.all([
    sdk.table('discount_codes_c').select(['Name']).where(eq('customer_c', customer.Id)).limit(200).fetch(),
    sdk
      .table('orders_c')
      .select(['line_username_c'])
      .where(and(eq('customer_c', customer.Id), isNotNull('line_username_c')))
      .limit(200)
      .fetch(),
  ]);
  rows(linked).forEach((d) => d.Name && codes.add(String(d.Name).trim().toUpperCase()));
  const names = new Set();
  rows(own).forEach((o) => {
    const n = normUsername(o.line_username_c);
    if (n) names.add(n);
  });

  const fields = [
    'Name',
    'customer_c',
    'email_c',
    'status_c',
    'plan_c',
    'term_c',
    'referral_counted_c',
    'discount_code_c',
    'referred_by_c',
  ];
  const lookups = [];
  if (codes.size) lookups.push(sdk.table('orders_c').select(fields).where(isIn('discount_code_c', [...codes])).limit(500).fetch());
  if (names.size) lookups.push(sdk.table('orders_c').select(fields).where(isIn('referred_by_c', [...names])).limit(500).fetch());
  const found = new Map();
  for (const res of await Promise.all(lookups)) rows(res).forEach((o) => found.set(String(o.Id), o));

  const earned = [...found.values()].filter(
    (o) =>
      (o.status_c === 'Active' || o.referral_counted_c) &&
      qualifiesForReferral(o) &&
      String(idOf(o.customer_c) ?? '') !== String(customer.Id) &&
      !sameText(o.email_c, customer.email_c),
  );

  const up = await sdk.table('customers_c').update({ Id: customer.Id, referrals_earned_c: earned.length });
  if (!up?.data?.length) throw new Error(up?.messages?.[0] || up?.message || 'Could not save the referral count');
  await Promise.all(
    earned
      .filter((o) => !o.referral_counted_c)
      .map((o) => sdk.table('orders_c').update({ Id: o.Id, referral_counted_c: true }).catch(() => null)),
  );
  return earned.length;
}
